import React, { useEffect, useState } from "react";
import { Button, IconButton, Typography } from "@mui/material";
import { DownloadRounded, DescriptionOutlined } from "@mui/icons-material";
import apiService from "../../../Services/APIService";
import FileIconPdf from "../../../../images/file-type-pdf.svg";
import { s360ConfigService } from "../../../Services/S360Config";
import { convertEpochToTimestamp } from "../../../../utils/dateConversion";
import "./NCXComponents.styles.css";
import flowMemorySingleton from "../../../Services/FlowMemorySingleton";

function NCXMediaView(props) {
  const {
    mediaData,
    id,
    agentText = false,
    downloadInProgress,
    creator,
    senderId,
    senderName,
    messageId,
    receivedTimestamp,
    workitemId,
  } = props;

  const [agentImage, setAgentImage] = useState(false);
  const [mediaUrls, setMediaUrls] = useState({});

  const senderNameStyle = {
    fontWeight: "bold",
    marginBottom: "0.25rem",
    fontSize: "x-small",
  };

  const calculateFileSize = (value) => {
    value = Math.floor(parseFloat(value / 1024));
    const KB_IN_MB = 1024;
    const KB_IN_GB = 1024 * 1024;

    if (value >= KB_IN_GB) return (value / KB_IN_GB).toFixed(2) + " gb";
    if (value >= KB_IN_MB) return (value / KB_IN_MB).toFixed(2) + " mb";
    return value + " kb";
  };

  const selectFileIcon = (attachmentMimeType) => {
    switch (attachmentMimeType) {
      case "application/pdf":
        return <img src={FileIconPdf} alt="PDF" />;
      default:
        return (
          <DescriptionOutlined fontSize="large" sx={{ color: "#7A7D8C" }} />
        );
    }
  };

  function convertGcsDownloadUrl(url) {
    try {
      const u = new URL(url);

      if (
        u.hostname === "storage.googleapis.com" &&
        u.pathname.startsWith("/download/storage/v1") &&
        u.searchParams.get("alt") === "media"
      ) {
        const bucketMatch = u.pathname.match(/\/b\/([^/]+)\/o\/(.+)/);
        if (!bucketMatch) return url;

        const bucket = bucketMatch[1];
        const objectPath = decodeURIComponent(bucketMatch[2]);

        return `https://storage.googleapis.com/${bucket}/${objectPath}`;
      }

      return url;
    } catch (e) {
      console.error("Invalid URL:", url, e);
      return url;
    }
  }

  const selectViewFromMediaType = (url, mData) => {
    const isImage = [".png", ".jpg", ".jpeg", ".gif"].some((ext) =>
      url.toLowerCase().includes(ext)
    );
    const isVideo = [".mp4", ".avi", ".mebm"].some((ext) =>
      url.toLowerCase().includes(ext)
    );
    const isAudio = [".mp3", ".wav", ".ogg", ".flac", ".aac"].some((ext) =>
      url.toLowerCase().includes(ext)
    ); // Added audio files detection

    if (isImage) {
      return (
        <img
          className={downloadInProgress ? "" : "mediaViewCommon"}
          style={
            downloadInProgress ? { width: "100px", objectFit: "contain" } : {}
          }
          src={url}
          alt={mData.title || mData.caption || "Media"}
          onClick={() => window.open(convertGcsDownloadUrl(url), "_blank")}
        />
      );
    }

    if (isVideo) {
      return (
        <video
          className="mediaViewCommon"
          controls
          onClick={() => window.open(convertGcsDownloadUrl(url), "_blank")}
        >
          <source src={url} />
        </video>
      );
    }
    if (isAudio) {
      return (
        <audio className="mediaViewCommon" controls>
          <source src={url} />
          Your browser does not support the audio element.
        </audio>
      );
    }
    return null;
  };

  const generateAgentAvatar = () => {
    if (s360ConfigService.config?.sandBox) {
      return (
        <svg
          width="30"
          height="30"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          style={{ marginRight: "8px" }}
        >
          <circle
            cx="50"
            cy="50"
            r="48"
            stroke="#D1D5DB"
            strokeWidth="4"
            fill="#F9FAFB"
          />
          <circle cx="50" cy="35" r="15" fill="#9CA3AF" />
          <path
            d="M20 80c0-16.57 13.43-30 30-30s30 13.43 30 30"
            fill="#9CA3AF"
          />
        </svg>
      );
    }
    if (agentImage) {
      return (
        <img
          src={agentImage}
          alt=""
          style={{
            width: "30px",
            height: "30px",
            borderRadius: "20px",
            marginRight: "8px",
            objectFit: "cover",
          }}
        />
      );
    }
  };

  const fetchUserImage = async (senderId, messageSubType) => {
    try {
      let imgData;

      if (messageSubType === "AIEMPLOYEE") {
        imgData = await apiService.getAIEmployeeImage(senderId);
        flowMemorySingleton.setCurrentAIEmployeeImageUrl(imgData?.data?.imageURL);
        flowMemorySingleton.setCurrentAIEmployeeUserId(senderId);
        setAgentImage(imgData?.data?.imageURL);
      } else {
        imgData = await apiService.getAgentImage(senderId);
        flowMemorySingleton.setCurrentAgentImageUrl(imgData?.data?.signedURL);
        flowMemorySingleton.setCurrentAgentUserId(senderId);
        setAgentImage(imgData?.data?.signedURL);
      }
    } catch (error) {
      console.error(`Failed to fetch ${messageSubType === "AIEMPLOYEE" ? "AI Employee" : "agent"} image:`, error);
      // Don't set image on error - will show name without avatar
    }
  };

  useEffect(() => {
    const messageSubType = props.message?.messageSubType;
    const isAgent = creator === "AGENT";
    const isAIEmployee = creator === "BOT" && messageSubType === "AIEMPLOYEE";

    if (isAgent || isAIEmployee) {
      if (isAIEmployee) {
        if (senderId !== flowMemorySingleton.getCurrentAIEmployeeUserId()) {
          fetchUserImage(senderId, messageSubType);
        } else {
          setAgentImage(flowMemorySingleton.getCurrentAIEmployeeImageUrl());
        }
      } else {
        if (senderId !== flowMemorySingleton.getCurrentAgentUserId()) {
          fetchUserImage(senderId, messageSubType);
        } else {
          setAgentImage(flowMemorySingleton.getCurrentAgentImageUrl());
        }
      }
    }
  }, [creator, senderId, props.message?.messageSubType]);

  const fetchAndStoreUrl = async (mData) => {
    try {
      const result = await apiService.getMediaUrl(workitemId, mData.title);
      const newUrl = result?.data?.signedURL;
      const key =
        mData.title || mData.mediaUrl || mData.id || Math.random().toString();
      if (newUrl && mediaUrls[key] !== newUrl) {
        setMediaUrls((prev) => ({ ...prev, [key]: newUrl }));
      }
    } catch (e) {
      console.warn("Media URL fetch failed:", e);
    }
  };

  const MediaViewItem = ({ mData }) => {
    const key =
      mData.title || mData.mediaUrl || mData.id || Math.random().toString();
    const urlInState = mediaUrls[key];

    useEffect(() => {
      if (!urlInState) {
        if (agentText) {
          fetchAndStoreUrl(mData);
        } else if (mData.mediaPath || mData.mediaUrl) {
          const directUrl = mData.mediaPath || mData.mediaUrl;
          setMediaUrls((prev) => ({ ...prev, [key]: directUrl }));
        }
      }
    }, [mData]);

    const url = mediaUrls[key];
    if (!url) return null;

    const fileName = mData.title || mData.caption || "download";
    const fileMimeType = mData.mediaMimeType || mData.fileType || "";
    const fileSize = mData.mediaSize;

    const isPreview = [
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".mp4",
      ".avi",
      ".mebm",
    ].some((type) => url.toLowerCase().includes(type));

    return (
      <div>
        {(creator === "AGENT" || (creator === "BOT" && props.message?.messageSubType === "AIEMPLOYEE")) && senderName && (
          <div
            style={{
              ...senderNameStyle,
              paddingLeft:
                !!agentImage &&
                s360ConfigService?.config?.configDetails?.showAgentInfo
                  ? "40px"
                  : "0",
            }}
            className="sender-name-agent"
          >
            {senderName()} - {convertEpochToTimestamp(receivedTimestamp)}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              creator === "AGENT" || creator === "BOT"
                ? "flex-start"
                : "flex-end",
          }}
        >
          {(creator === "AGENT" || (creator === "BOT" && props.message?.messageSubType === "AIEMPLOYEE")) &&
            senderId &&
            s360ConfigService?.config?.configDetails?.showAgentInfo &&
            generateAgentAvatar()}
          <div className="mediaContainer">
            {isPreview ? (
              <div className="mediaWrapper">
                {selectViewFromMediaType(url, { title: fileName })}
              </div>
            ) : (
              <div className="mediaViewNonPreview">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {selectFileIcon(fileMimeType)}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      style={{
                        fontWeight: "800",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "39vw",
                      }}
                    >
                      {fileName}
                    </span>
                    {fileSize && <span>{calculateFileSize(fileSize)}</span>}
                  </div>
                </div>
                <IconButton
                  onClick={() => {
                    if (url.includes(".pdf")) {
                      window.open(url, "_blank");
                    } else if (
                      url.includes(".wav") ||
                      url.includes(".mp3") ||
                      url.includes(".ogg") ||
                      url.includes(".flac") ||
                      url.includes(".aac")
                    ) {
                      // Handle audio files specifically
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = fileName || "audio-file";
                      link.setAttribute("type", "audio/*");
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                    //handle for video files as well
                    else if (
                      url.includes(".mp4") ||
                      url.includes(".avi") ||
                      url.includes(".mebm")
                    ) {
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = fileName || "video-file";
                      link.setAttribute("type", "video/*");
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    } else {
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                >
                  <DownloadRounded />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="mediaViewContainer"
      style={{
        alignItems:
          creator === "AGENT" || creator === "BOT" ? "flex-start" : "flex-end",
      }}
    >
      {mediaData.map((mData, index) => (
        <MediaViewItem
          key={mData.title || mData.mediaUrl || mData.id || index}
          mData={mData}
        />
      ))}
    </div>
  );
}

export default NCXMediaView;
